import { useCallback, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../store';
import {
  setCredentials,
  logout as logoutAction,
  setLoading,
  setError,
  setInitialized,
} from '../store/slices';
import * as cognito from '../services/cognito';

/**
 * Hook for authentication state and actions backed by AWS Cognito.
 */
export function useAuth() {
  const dispatch = useAppDispatch();
  const { user, token, accessToken, isAuthenticated, isInitializing, isLoading, error } =
    useAppSelector((state) => state.auth);

  // Track whether session restoration has been attempted
  const restoredRef = useRef(false);

  // ------------------------------------------------------------------
  // On mount: try to restore the Cognito session from localStorage
  // ------------------------------------------------------------------
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;

    cognito
      .getCurrentSession()
      .then((result) => {
        if (result) {
          dispatch(
            setCredentials({
              user: {
                id: result.user.userId,
                email: result.user.email,
                name: result.user.name,
                organizationId: result.user.organizationId,
              },
              token: result.tokens.idToken,
              accessToken: result.tokens.accessToken,
              refreshToken: result.tokens.refreshToken,
            })
          );
        } else {
          dispatch(setInitialized());
        }
      })
      .catch(() => {
        dispatch(setInitialized());
      });
  }, [dispatch]);

  // ------------------------------------------------------------------
  // Sign in
  // ------------------------------------------------------------------
  const login = useCallback(
    async (email: string, password: string) => {
      dispatch(setLoading(true));
      dispatch(setError(''));
      try {
        const result = await cognito.signIn(email, password);
        dispatch(
          setCredentials({
            user: {
              id: result.user.userId,
              email: result.user.email,
              name: result.user.name,
              organizationId: result.user.organizationId,
            },
            token: result.tokens.idToken,
            accessToken: result.tokens.accessToken,
            refreshToken: result.tokens.refreshToken,
          })
        );
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Login failed. Please try again.';
        dispatch(setError(message));
      }
    },
    [dispatch]
  );

  // ------------------------------------------------------------------
  // Sign up
  // ------------------------------------------------------------------
  const signup = useCallback(
    async (email: string, password: string, name: string) => {
      dispatch(setLoading(true));
      dispatch(setError(''));
      try {
        await cognito.signUp(email, password, name);
        dispatch(setLoading(false));
        // Return email so the UI can transition to the verification step
        return email;
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Sign-up failed. Please try again.';
        dispatch(setError(message));
        return null;
      }
    },
    [dispatch]
  );

  // ------------------------------------------------------------------
  // Confirm sign-up (verification code)
  // ------------------------------------------------------------------
  const confirmSignup = useCallback(
    async (email: string, code: string, password: string) => {
      dispatch(setLoading(true));
      dispatch(setError(''));
      try {
        await cognito.confirmSignUp(email, code);
        // Auto-sign in after confirmation
        const result = await cognito.signIn(email, password);
        dispatch(
          setCredentials({
            user: {
              id: result.user.userId,
              email: result.user.email,
              name: result.user.name,
              organizationId: result.user.organizationId,
            },
            token: result.tokens.idToken,
            accessToken: result.tokens.accessToken,
            refreshToken: result.tokens.refreshToken,
          })
        );
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Verification failed. Please try again.';
        dispatch(setError(message));
      }
    },
    [dispatch]
  );

  // ------------------------------------------------------------------
  // Resend confirmation code
  // ------------------------------------------------------------------
  const resendCode = useCallback(
    async (email: string) => {
      dispatch(setError(''));
      try {
        await cognito.resendConfirmationCode(email);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Could not resend code.';
        dispatch(setError(message));
      }
    },
    [dispatch]
  );

  // ------------------------------------------------------------------
  // Logout
  // ------------------------------------------------------------------
  const logout = useCallback(() => {
    cognito.signOut();
    dispatch(logoutAction());
  }, [dispatch]);

  return {
    user,
    token,
    accessToken,
    isAuthenticated,
    isInitializing,
    isLoading,
    error,
    login,
    signup,
    confirmSignup,
    resendCode,
    logout,
  };
}
